<script setup lang="ts">
defineOptions({ name: 'LoginView' })
import PasswordInput from '../components/PasswordInput.vue'
import PasswordChecklist from '../components/PasswordChecklist.vue'
import { meetsPasswordPolicy } from '../utils/passwordPolicy'
import { onMounted, ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { isLocalDev, isDevEnvironment, TEST_USERS, type TestUser } from '../composables/useDevAuth'
import { setDevToken } from '../composables/useAuth'
import { safeReturnTo } from '../router/authGuards'
import { authLogin, authSignup, authForgotPassword, validateClaimCode, validateAdvisorInviteCode } from '../services/api'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const { loginWithRedirect, isAuthenticated, isLoading, user: auth0User, getAccessTokenSilently } = useAuth0()

// View modes
type ViewMode = 'login' | 'signup' | 'forgot-password'
const mode = ref<ViewMode>('login')

// Shared state
const checkingAuth = ref(true)
const errorMessage = ref<string | null>(null)
const successMessage = ref<string | null>(null)

// Login form
const loginEmail = ref('')
const loginPassword = ref('')
const loginLoading = ref(false)

// Signup form
type SignupPath = 'supplier-claim' | 'advisor-invite' | null
const signupPath = ref<SignupPath>(null)
const signupCode = ref('')
const signupValidating = ref(false)
const signupCodeValidated = ref(false)
const signupSupplierName = ref<string | null>(null)
const signupEmail = ref('')
const signupPassword = ref('')
const signupName = ref('')
const signupFirmName = ref('')
const signupLoading = ref(false)
const signupEmailTouched = ref(false)
const signupNameTouched = ref(false)
const signupSubmitted = ref(false)
const signupEmailInput = ref<HTMLInputElement | null>(null)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const signupEmailValid = computed(() => EMAIL_RE.test(signupEmail.value.trim()))
const signupNameValid = computed(() => signupName.value.trim().length > 0)
const showSignupEmailError = computed(() => signupEmailTouched.value && !signupEmailValid.value && (signupEmail.value !== '' || signupSubmitted.value))
const showSignupNameError = computed(() => signupNameTouched.value && !signupNameValid.value && (signupName.value !== '' || signupSubmitted.value))

// Forgot password form
const forgotEmail = ref('')
const forgotLoading = ref(false)
const forgotSent = ref(false)

// Dev login
const quickLoginLoading = ref<string | null>(null)
const devLoginEmail = ref('')
const devLoginPassword = ref('')
const devLoginLoading = ref(false)

// Computed
const isRemoteDev = isDevEnvironment && !isLocalDev

const modeTitle = computed(() => {
    switch (mode.value) {
        case 'login': return 'Sign in'
        case 'signup': return 'Create an account'
        case 'forgot-password': return 'Reset password'
        default: return ''
    }
})

// Error from redirect
if (route.query.error === 'signup_required') {
    mode.value = 'signup'
    errorMessage.value = 'An invite or claim code is required to create an account.'
} else if (route.query.error === 'suspended') {
    errorMessage.value = 'This account has been suspended. Contact your administrator if you believe this is a mistake.'
}

// Check if already authenticated
onMounted(async () => {
    while (isLoading.value) {
        await new Promise(resolve => setTimeout(resolve, 100))
    }

    // Auth0 session without a store user (page reload). The router guest guard
    // already redirected sessions it could resolve; keep the spinner up here so
    // the form never flashes before the redirect.
    if (isAuthenticated.value && auth0User.value) {
        try {
            const token = await getAccessTokenSilently()
            const result = await userStore.initUser(token, auth0User.value.email!, auth0User.value.name || null)
            if (result.needsSignup) {
                mode.value = 'signup'
            } else {
                router.replace(safeReturnTo(route.query.returnTo) ?? userStore.getOnboardingRoute())
                return
            }
        } catch (e) {
            // Expired/invalid session — fall through and show the form
            console.error('Failed to check user:', e)
        }
    }
    checkingAuth.value = false
})

// ============================================
// Email/Password Login
// ============================================

async function handleEmailLogin() {
    errorMessage.value = null
    loginLoading.value = true

    try {
        const tokenData = await authLogin(loginEmail.value, loginPassword.value)
        setDevToken(tokenData.access_token)

        const result = await userStore.initUser(tokenData.access_token, loginEmail.value, null)
        if (result.needsSignup) {
            mode.value = 'signup'
        } else {
            router.push(userStore.getOnboardingRoute())
        }
    } catch (e) {
        const err = e as { response?: { data?: { error?: string } }; message?: string }
        errorMessage.value = err.response?.data?.error || 'Invalid email or password'
    } finally {
        loginLoading.value = false
    }
}

// ============================================
// Social Login (Google, Microsoft)
// ============================================

function handleSocialLogin(connection: string) {
    errorMessage.value = null
    const returnTo = route.query.returnTo as string || '/auth/callback'
    loginWithRedirect({
        appState: { target: returnTo },
        authorizationParams: {
            connection,
            screen_hint: 'login'
        }
    })
}

// ============================================
// Signup Flow
// ============================================

function selectSignupPath(path: SignupPath) {
    signupPath.value = path
    signupCode.value = ''
    signupCodeValidated.value = false
    signupSupplierName.value = null
    signupEmailTouched.value = false
    signupNameTouched.value = false
    signupSubmitted.value = false
    errorMessage.value = null

    // Advisors no longer need an invite code — skip validation step
    if (path === 'advisor-invite') {
        signupCodeValidated.value = true
    }
}

async function validateCode() {
    if (!signupCode.value.trim()) return
    signupValidating.value = true
    errorMessage.value = null

    try {
        if (signupPath.value === 'supplier-claim') {
            const result = await validateClaimCode(signupCode.value.trim())
            if (result.valid && result.supplier) {
                signupCodeValidated.value = true
                signupSupplierName.value = result.supplier.name
            } else {
                errorMessage.value = result.error || 'Invalid claim code'
            }
        } else if (signupPath.value === 'advisor-invite') {
            const result = await validateAdvisorInviteCode(signupCode.value.trim())
            if (result.valid) {
                signupCodeValidated.value = true
            } else {
                errorMessage.value = result.error || 'Invalid invite code'
            }
        }
    } catch (e) {
        const err = e as { response?: { data?: { error?: string } }; message?: string }
        errorMessage.value = err.response?.data?.error || 'Failed to validate code'
    } finally {
        signupValidating.value = false
    }
}

async function handleEmailSignup() {
    signupEmailTouched.value = true
    signupNameTouched.value = true
    signupSubmitted.value = true
    if (!signupEmailValid.value) {
        signupEmailInput.value?.focus()
        return
    }
    if (!signupNameValid.value) return

    errorMessage.value = null
    signupLoading.value = true

    try {
        const tokenData = await authSignup({
            email: signupEmail.value,
            password: signupPassword.value,
            name: signupName.value,
            signupType: signupPath.value!,
            code: signupPath.value === 'advisor-invite' ? '' : signupCode.value.trim(),
            advisorDetails: signupPath.value === 'advisor-invite' && signupFirmName.value
                ? { firmName: signupFirmName.value }
                : undefined,
        })

        if (tokenData.access_token) {
            setDevToken(tokenData.access_token)
            await userStore.initUser(tokenData.access_token, signupEmail.value, signupName.value)
            router.push(userStore.getOnboardingRoute())
        } else {
            // Token wasn't returned — user created but needs to sign in
            successMessage.value = 'Account created! Please sign in.'
            mode.value = 'login'
            loginEmail.value = signupEmail.value
        }
    } catch (e) {
        const err = e as { response?: { data?: { error?: string } }; message?: string }
        errorMessage.value = err.response?.data?.error || 'Failed to create account'
    } finally {
        signupLoading.value = false
    }
}

function handleSocialSignup() {
    // Store code for AuthCallback to use
    const pendingSignup = {
        path: signupPath.value,
        code: signupPath.value === 'advisor-invite' ? '' : signupCode.value.trim(),
        supplierName: signupSupplierName.value
    }
    localStorage.setItem('cs_pending_signup', JSON.stringify(pendingSignup))

    loginWithRedirect({
        appState: { target: '/auth/callback' },
        authorizationParams: {
            screen_hint: 'signup'
        }
    })
}

// ============================================
// Forgot Password
// ============================================

async function handleForgotPassword() {
    errorMessage.value = null
    forgotLoading.value = true

    try {
        await authForgotPassword(forgotEmail.value)
        forgotSent.value = true
    } catch {
        // Always show success to prevent email enumeration
        forgotSent.value = true
    } finally {
        forgotLoading.value = false
    }
}

// ============================================
// Dev Quick Login (localhost + remote dev)
// ============================================

const TEST_PASSWORD = 'TestPass123!'

async function handleQuickLogin(user: TestUser) {
    errorMessage.value = null
    quickLoginLoading.value = user.email

    try {
        const tokenData = await authLogin(user.email, TEST_PASSWORD)
        setDevToken(tokenData.access_token)

        const result = await userStore.initUser(tokenData.access_token, user.email, user.name)
        if (result.needsSignup) {
            mode.value = 'signup'
        } else {
            router.push(userStore.getOnboardingRoute())
        }
    } catch (e) {
        const err = e as { response?: { data?: { error?: string } }; message?: string }
        errorMessage.value = err.response?.data?.error || err.message || 'Login failed'
    } finally {
        quickLoginLoading.value = null
    }
}

async function handleRemoteDevLogin() {
    errorMessage.value = null
    devLoginLoading.value = true

    try {
        const tokenData = await authLogin(devLoginEmail.value, devLoginPassword.value)
        setDevToken(tokenData.access_token)

        const result = await userStore.initUser(tokenData.access_token, devLoginEmail.value, null)
        if (result.needsSignup) {
            mode.value = 'signup'
        } else {
            router.push(userStore.getOnboardingRoute())
        }
    } catch (e) {
        const err = e as { response?: { data?: { error?: string } }; message?: string }
        errorMessage.value = err.response?.data?.error || err.message || 'Login failed'
    } finally {
        devLoginLoading.value = false
    }
}

function switchMode(newMode: ViewMode) {
    mode.value = newMode
    errorMessage.value = null
    successMessage.value = null
    forgotSent.value = false
    if (newMode === 'signup') {
        signupPath.value = null
        signupCodeValidated.value = false
    }
}

function getRoleBadgeClass(role: string) {
    switch (role) {
        case 'ADMIN': return 'bg-cc-primary/10 text-cc-primary'
        case 'ADVISOR': return 'bg-cc-accent/10 text-cc-accent'
        case 'SUPPLIER_ADMIN': return 'bg-cc-success/10 text-cc-success'
        default: return 'bg-cc-surface text-cc-text-secondary'
    }
}
</script>

<template>
    <div class="min-h-screen bg-cc-deep flex items-center justify-center p-4">
        <div class="bg-cc-elevated rounded-2xl shadow-cc-card max-w-md w-full p-8 border border-cc-border">
            <!-- Loading state -->
            <div v-if="checkingAuth" class="text-center">
                <div class="animate-spin w-12 h-12 border-4 border-cc-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p class="text-cc-text-secondary">Checking authentication...</p>
            </div>

            <div v-else>
                <!-- Header -->
                <div class="text-center mb-6">
                    <!-- Login: the TCS globe only, in dark ink, at least 72px (Alex's brand rules). No Companion lockup here. -->
                    <img src="/assets/tcs-globe-on-light.svg" alt="The Channel Standard" class="h-20 w-auto mx-auto mb-3" />
                    <h1 class="text-lg font-semibold text-cc-text">{{ modeTitle }}</h1>
                    <p class="text-cc-text-secondary text-sm mt-1">Connect advisors with suppliers</p>
                </div>

                <!-- Success banner -->
                <div v-if="successMessage" class="mb-4 p-3 bg-cc-success/10 border border-cc-success/20 rounded-cc">
                    <p class="text-cc-success text-sm">{{ successMessage }}</p>
                </div>

                <!-- Error banner -->
                <div v-if="errorMessage" class="mb-4 p-3 bg-cc-danger/10 border border-cc-danger/20 rounded-cc">
                    <p class="text-cc-danger text-sm">{{ errorMessage }}</p>
                </div>

                <!-- ========== LOGIN MODE ========== -->
                <template v-if="mode === 'login'">
                    <!-- Social login buttons -->
                    <div class="space-y-3 mb-5">
                        <button
                            @click="handleSocialLogin('google-oauth2')"
                            class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-cc-border rounded-cc hover:bg-cc-surface transition-colors"
                        >
                            <svg class="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span class="text-cc-text font-medium text-sm">Continue with Google</span>
                        </button>

                        <button
                            @click="handleSocialLogin('windowslive')"
                            class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-cc-border rounded-cc hover:bg-cc-surface transition-colors"
                        >
                            <svg class="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#F25022" d="M1 1h10v10H1z"/>
                                <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                                <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                                <path fill="#FFB900" d="M13 13h10v10H13z"/>
                            </svg>
                            <span class="text-cc-text font-medium text-sm">Continue with Microsoft</span>
                        </button>
                    </div>

                    <!-- Divider -->
                    <div class="flex items-center gap-3 mb-5">
                        <div class="flex-1 h-px bg-cc-border"></div>
                        <span class="text-xs text-cc-text-tertiary uppercase tracking-wider">or</span>
                        <div class="flex-1 h-px bg-cc-border"></div>
                    </div>

                    <!-- Email/password form -->
                    <form @submit.prevent="handleEmailLogin" class="space-y-4">
                        <div>
                            <label for="login-email" class="block text-sm font-medium text-cc-text text-left mb-1">Email</label>
                            <input
                                id="login-email"
                                v-model="loginEmail"
                                type="email"
                                required
                                autocomplete="email"
                                placeholder="you@company.com"
                                class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                            />
                        </div>
                        <div>
                            <div class="flex items-center justify-between mb-1">
                                <label for="login-password" class="block text-sm font-medium text-cc-text">Password</label>
                                <button type="button" @click="switchMode('forgot-password')" class="text-xs text-cc-primary hover:underline">
                                    Forgot password?
                                </button>
                            </div>
                            <PasswordInput
                                id="login-password"
                                v-model="loginPassword"
                                required
                                autocomplete="current-password"
                                placeholder="Enter your password"
                                class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                            />
                        </div>
                        <button
                            type="submit"
                            :disabled="loginLoading"
                            class="w-full px-4 py-3 bg-cc-primary text-cc-btn-text rounded-cc hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                        >
                            <div v-if="loginLoading" class="animate-spin w-4 h-4 border-2 border-cc-btn-text border-t-transparent rounded-full"></div>
                            {{ loginLoading ? 'Signing in...' : 'Sign in' }}
                        </button>
                    </form>

                    <!-- Switch to signup -->
                    <div class="mt-6 pt-5 border-t border-cc-border text-center">
                        <p class="text-sm text-cc-text-secondary">
                            Don't have an account?
                            <button @click="switchMode('signup')" class="text-cc-primary hover:underline font-medium">
                                Sign up
                            </button>
                        </p>
                    </div>
                </template>

                <!-- ========== SIGNUP MODE ========== -->
                <template v-else-if="mode === 'signup'">
                    <!-- Step 1: Choose path -->
                    <div v-if="!signupPath" class="space-y-3">
                        <p class="text-sm text-cc-text-secondary text-center mb-4">Select your account type</p>
                        <!-- Supplier claim removed as a visible path; direct claim
                             links (?claim=CODE) still work via AuthCallback. -->
                        <button
                            @click="selectSignupPath('advisor-invite')"
                            class="w-full flex items-center gap-3 px-4 py-3 border border-cc-border rounded-cc hover:border-cc-primary hover:bg-cc-primary/5 transition-colors text-left"
                        >
                            <div class="w-8 h-8 bg-cc-primary/10 rounded-cc-sm flex items-center justify-center shrink-0">
                                <svg class="w-4 h-4 text-cc-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                            </div>
                            <div>
                                <p class="text-sm font-medium text-cc-text">Advisor</p>
                                <p class="text-xs text-cc-text-tertiary">Join as a technology advisor</p>
                            </div>
                        </button>
                        <router-link
                            to="/cm/signup"
                            class="w-full flex items-center gap-3 px-4 py-3 border border-cc-border rounded-cc hover:border-amber-500 hover:bg-amber-500/5 transition-colors text-left"
                        >
                            <div class="w-8 h-8 bg-amber-500/10 rounded-cc-sm flex items-center justify-center shrink-0">
                                <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <div>
                                <p class="text-sm font-medium text-cc-text">Channel Manager</p>
                                <p class="text-xs text-cc-text-tertiary">Manage your partner relationships</p>
                            </div>
                        </router-link>
                    </div>

                    <!-- Step 2: Enter code -->
                    <div v-else-if="!signupCodeValidated" class="space-y-3">
                        <p class="text-sm font-medium text-cc-text">
                            {{ signupPath === 'supplier-claim' ? 'Enter your claim code' : 'Enter your invite code' }}
                        </p>
                        <div class="flex gap-2">
                            <input
                                v-model="signupCode"
                                type="text"
                                :placeholder="signupPath === 'supplier-claim' ? 'CLAIM-XXXXXX' : 'ADV-XXXXXX'"
                                class="flex-1 px-3 py-2 border border-cc-border rounded-cc bg-cc-elevated text-cc-text text-sm uppercase placeholder:normal-case focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none"
                                @keyup.enter="validateCode"
                            />
                            <button
                                @click="validateCode"
                                :disabled="signupValidating || !signupCode.trim()"
                                class="px-4 py-2 text-sm bg-cc-primary text-cc-btn-text rounded-cc hover:opacity-90 disabled:opacity-50 transition-colors"
                            >
                                {{ signupValidating ? '...' : 'Verify' }}
                            </button>
                        </div>
                    </div>

                    <!-- Step 3: Code validated — show signup form -->
                    <div v-else class="space-y-4">
                        <!-- Validated badge -->
                        <div v-if="signupPath === 'supplier-claim'" class="p-3 rounded-cc text-sm bg-cc-success/10 border border-cc-success/20">
                            <div class="flex items-center gap-2">
                                <svg class="w-4 h-4 shrink-0 text-cc-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                                <span class="text-cc-success font-medium">Claiming: {{ signupSupplierName }}</span>
                            </div>
                        </div>
                        <div v-else class="p-3 rounded-cc text-sm bg-cc-primary/10 border border-cc-primary/20">
                            <div class="flex items-center gap-2">
                                <svg class="w-4 h-4 shrink-0 text-cc-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                                <span class="text-cc-primary font-medium">Create your advisor account</span>
                            </div>
                        </div>

                        <!-- Social signup buttons -->
                        <div class="space-y-3">
                            <button
                                @click="handleSocialSignup"
                                class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-cc-border rounded-cc hover:bg-cc-surface transition-colors"
                            >
                                <svg class="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                <span class="text-cc-text font-medium text-sm">Sign up with Google</span>
                            </button>

                            <button
                                @click="handleSocialSignup"
                                class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-cc-border rounded-cc hover:bg-cc-surface transition-colors"
                            >
                                <svg class="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="#F25022" d="M1 1h10v10H1z"/>
                                    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                                    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                                    <path fill="#FFB900" d="M13 13h10v10H13z"/>
                                </svg>
                                <span class="text-cc-text font-medium text-sm">Sign up with Microsoft</span>
                            </button>
                        </div>

                        <!-- Divider -->
                        <div class="flex items-center gap-3">
                            <div class="flex-1 h-px bg-cc-border"></div>
                            <span class="text-xs text-cc-text-tertiary uppercase tracking-wider">or</span>
                            <div class="flex-1 h-px bg-cc-border"></div>
                        </div>

                        <!-- Email signup form -->
                        <form @submit.prevent="handleEmailSignup" novalidate class="space-y-3">
                            <div>
                                <label class="block text-sm font-medium text-cc-text text-left mb-1">Full name</label>
                                <input
                                    v-model="signupName"
                                    type="text"
                                    autocomplete="name"
                                    placeholder="Jane Smith"
                                    :aria-invalid="showSignupNameError || undefined"
                                    :aria-describedby="showSignupNameError ? 'signup-name-error' : undefined"
                                    @blur="signupNameTouched = true"
                                    class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                                    :class="{ '!border-rose-500': showSignupNameError }"
                                />
                                <p v-if="showSignupNameError" id="signup-name-error" class="mt-1 text-xs text-left text-rose-600 dark:text-rose-400">Enter your name.</p>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-cc-text text-left mb-1">Email</label>
                                <input
                                    ref="signupEmailInput"
                                    v-model="signupEmail"
                                    type="email"
                                    autocomplete="email"
                                    placeholder="you@company.com"
                                    :aria-invalid="showSignupEmailError || undefined"
                                    :aria-describedby="showSignupEmailError ? 'signup-email-error' : undefined"
                                    @blur="signupEmailTouched = true"
                                    class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                                    :class="{ '!border-rose-500': showSignupEmailError }"
                                />
                                <p v-if="showSignupEmailError" id="signup-email-error" class="mt-1 text-xs text-left text-rose-600 dark:text-rose-400">Enter a valid email address.</p>
                            </div>
                            <div>
                                <label for="signup-password" class="block text-sm font-medium text-cc-text text-left mb-1">Password</label>
                                <PasswordInput
                                    id="signup-password"
                                    v-model="signupPassword"
                                    required
                                    autocomplete="new-password"
                                    aria-describedby="signup-password-rules"
                                    class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                                />
                                <PasswordChecklist id="signup-password-rules" :password="signupPassword" class="text-left" />
                            </div>
                            <div v-if="signupPath === 'advisor-invite'">
                                <label class="block text-sm font-medium text-cc-text text-left mb-1">Firm name <span class="text-cc-text-tertiary font-normal">(optional)</span></label>
                                <input
                                    v-model="signupFirmName"
                                    type="text"
                                    placeholder="Your advisory firm"
                                    class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                                />
                            </div>
                            <button
                                type="submit"
                                :disabled="signupLoading || !meetsPasswordPolicy(signupPassword)"
                                class="w-full px-4 py-3 bg-cc-primary text-cc-btn-text rounded-cc hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                            >
                                <div v-if="signupLoading" class="animate-spin w-4 h-4 border-2 border-cc-btn-text border-t-transparent rounded-full"></div>
                                {{ signupLoading ? 'Creating account...' : 'Create account' }}
                            </button>
                            <p v-if="signupPassword && !meetsPasswordPolicy(signupPassword)" class="text-xs text-cc-text-tertiary">Complete the password rules above to continue.</p>
                        </form>
                    </div>

                    <!-- Back / switch to login -->
                    <div class="mt-6 pt-5 border-t border-cc-border text-center space-y-2">
                        <button v-if="signupPath" @click="selectSignupPath(null)" class="text-sm text-cc-text-secondary hover:text-cc-text">
                            &larr; Back
                        </button>
                        <p class="text-sm text-cc-text-secondary">
                            Already have an account?
                            <button @click="switchMode('login')" class="text-cc-primary hover:underline font-medium">
                                Sign in
                            </button>
                        </p>
                    </div>
                </template>

                <!-- ========== FORGOT PASSWORD MODE ========== -->
                <template v-else-if="mode === 'forgot-password'">
                    <div v-if="!forgotSent">
                        <p class="text-sm text-cc-text-secondary text-center mb-5">Enter your email and we'll send you a reset link.</p>
                        <form @submit.prevent="handleForgotPassword" class="space-y-4">
                            <div>
                                <label for="forgot-email" class="block text-sm font-medium text-cc-text text-left mb-1">Email</label>
                                <input
                                    id="forgot-email"
                                    v-model="forgotEmail"
                                    type="email"
                                    required
                                    autocomplete="email"
                                    placeholder="you@company.com"
                                    class="w-full px-4 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                                />
                            </div>
                            <button
                                type="submit"
                                :disabled="forgotLoading"
                                class="w-full px-4 py-3 bg-cc-primary text-cc-btn-text rounded-cc hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                            >
                                <div v-if="forgotLoading" class="animate-spin w-4 h-4 border-2 border-cc-btn-text border-t-transparent rounded-full"></div>
                                {{ forgotLoading ? 'Sending...' : 'Send reset link' }}
                            </button>
                        </form>
                    </div>
                    <div v-else class="text-center">
                        <div class="w-12 h-12 bg-cc-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="w-6 h-6 text-cc-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <p class="text-cc-text font-medium">Check your email</p>
                        <p class="text-cc-text-secondary text-sm mt-1">If an account exists, we've sent a password reset link.</p>
                    </div>

                    <div class="mt-6 pt-5 border-t border-cc-border text-center">
                        <button @click="switchMode('login')" class="text-sm text-cc-primary hover:underline font-medium">
                            &larr; Back to sign in
                        </button>
                    </div>
                </template>

                <!-- ========== DEV LOGIN SECTION ========== -->
                <!-- Remote dev login (dev.* or staging.*) -->
                <div v-if="isRemoteDev && mode === 'login'" class="mt-6">
                    <div class="bg-cc-warning/10 border border-cc-warning/20 rounded-cc p-3 mb-4">
                        <p class="text-cc-warning text-sm font-medium">Development Environment</p>
                        <p class="text-cc-warning/70 text-xs">Login with test account credentials</p>
                    </div>
                    <form @submit.prevent="handleRemoteDevLogin" class="space-y-3">
                        <div>
                            <input
                                v-model="devLoginEmail"
                                type="email"
                                required
                                placeholder="admin@channelstandard.dev"
                                class="w-full px-4 py-2 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                            />
                        </div>
                        <div>
                            <PasswordInput
                                v-model="devLoginPassword"
                                required
                                placeholder="Password"
                                class="w-full px-4 py-2 border border-cc-border rounded-cc bg-cc-elevated text-cc-text focus:ring-2 focus:ring-cc-primary focus:border-cc-primary focus:outline-none text-sm"
                            />
                        </div>
                        <button
                            type="submit"
                            :disabled="devLoginLoading"
                            class="w-full px-4 py-2.5 bg-cc-warning/20 text-cc-warning rounded-cc hover:bg-cc-warning/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
                        >
                            <div v-if="devLoginLoading" class="animate-spin w-4 h-4 border-2 border-cc-warning border-t-transparent rounded-full"></div>
                            {{ devLoginLoading ? 'Signing in...' : 'Dev Login' }}
                        </button>
                    </form>
                </div>

                <!-- Local dev quick login (localhost only) -->
                <div v-else-if="isLocalDev && mode === 'login'" class="mt-6">
                    <div class="bg-cc-warning/10 border border-cc-warning/20 rounded-cc p-3 mb-4">
                        <p class="text-cc-warning text-sm font-medium">Local Development</p>
                        <p class="text-cc-warning/70 text-xs">One-click login with test accounts</p>
                    </div>
                    <div class="space-y-2">
                        <button
                            v-for="user in TEST_USERS"
                            :key="user.email"
                            @click="handleQuickLogin(user)"
                            :disabled="quickLoginLoading !== null"
                            class="w-full flex items-center justify-between px-4 py-3 bg-cc-surface hover:bg-cc-deep border border-cc-border rounded-cc transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div>
                                <p class="font-medium text-cc-text text-sm">{{ user.name }}</p>
                                <p class="text-xs text-cc-text-secondary">{{ user.email }}</p>
                            </div>
                            <div class="flex items-center gap-2">
                                <div v-if="quickLoginLoading === user.email" class="animate-spin w-4 h-4 border-2 border-cc-primary border-t-transparent rounded-full"></div>
                                <span :class="['px-2 py-1 text-xs font-medium rounded', getRoleBadgeClass(user.role)]">
                                    {{ user.role }}
                                </span>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- Data Privacy Notice (login mode only) -->
                <div v-if="mode === 'login'" class="mt-6 p-4 bg-cc-surface border border-cc-border rounded-cc text-left">
                    <div class="flex items-start gap-3">
                        <svg class="w-5 h-5 text-cc-primary mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <div>
                            <p class="text-sm font-semibold text-cc-text mb-1">Your data is private and secure.</p>
                            <p class="text-xs text-cc-text-tertiary leading-relaxed">
                                The information you store in Channel Standard — including your client accounts, contacts, deals, and pipeline — is exclusively yours and is never shared with other advisors, suppliers, or TSDs. We use industry-standard encryption and strict access controls to keep your data safe.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
